import { useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  CurecallModalContainer,
  CurecallSearchableSelect,
  CurecallButton,
  ECurecallButtonAppearance,
  CurecallMultiSelect,
  CurecallSearchableSelectItem,
  ECurecallNotificationAppearance,
  TOptionWithNumberValue,
} from '@honestica/curecall-ui'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useTranslation } from 'react-i18next'
import { TLabel, TNavigationLocales } from 'src/types/locales'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ProjectService } from 'src/services/project.service'
import { getTranslatedLabel } from 'src/utils/locale.util'
import { TFollowup } from 'src/types/followup'
import { TMedEventUI } from 'src/types/med-event'
import { getErrorMessage } from 'src/utils/http.util'
import { useNotification } from 'src/providers/notification.provider'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useLocale } from 'src/hooks/use-locale'

type TFollowupWithIntlName = TFollowup & {
  intlName?: TLabel
}

type TDuplicateMessageFormValues = {
  projectId: number
  followupId: number
  medEventIds: number[]
}

type Props = {
  isOpen: boolean
  onClose: () => void
  message: {
    id: number
    name: string
    followupId: number | null
  }
}

export function DuplicateProgrammedMessageModal({ isOpen, onClose, message }: Readonly<Props>) {
  const { t } = useTranslation()
  const { project, followups: currentProjectFollowups, medEvents } = useCurrentProject()
  const { followupId } = useParams()
  const { locale } = useLocale()
  // The source followup is the one the message actually belongs to, not the URL
  // param: the programmed-message list is project-wide and shows messages from
  // several followups, so `useParams().followupId` is not a reliable baseline.
  const sourceFollowupId = message.followupId ?? Number(followupId)
  const { notify } = useNotification()
  const navigate = useLocalizedNavigate()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)

  const methods = useForm<TDuplicateMessageFormValues>({
    defaultValues: {
      projectId: project.id,
      followupId: sourceFollowupId,
      medEventIds: [],
    },
  })

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = methods
  const watchedProjectId = watch('projectId')
  const watchedFollowupId = watch('followupId')

  const { data: userProjects = [] } = useQuery({
    queryKey: ['project-list'],
    queryFn: () => ProjectService.getProjectsList(),
  })
  const { data: otherProjectData } = useQuery({
    queryKey: ['project', watchedProjectId],
    queryFn: () => ProjectService.getById(watchedProjectId),
    enabled: watchedProjectId !== project.id,
  })

  // Get followups of the selected project
  const followupsOfTargetProject: TFollowupWithIntlName[] = useMemo(() => {
    if (watchedProjectId === project.id) {
      return currentProjectFollowups as TFollowupWithIntlName[]
    }
    return (otherProjectData?.Followups || []) as TFollowupWithIntlName[]
  }, [watchedProjectId, project.id, currentProjectFollowups, otherProjectData])

  const PROJECT_OPTIONS: CurecallSearchableSelectItem[] = useMemo(
    () => userProjects.map((userProject) => ({ value: userProject.id, label: userProject.name })),
    [userProjects],
  )
  const FOLLOWUP_OPTIONS: CurecallSearchableSelectItem[] = useMemo(
    () =>
      followupsOfTargetProject.map((followup) => ({
        value: followup.id,
        label:
          getTranslatedLabel(locale as TNavigationLocales, followup.intlName) ||
          followup.name ||
          `Followup ${followup.id}`,
      })),
    [followupsOfTargetProject, locale],
  )
  const MED_EVENT_OPTIONS: TOptionWithNumberValue[] = useMemo(() => {
    if (!watchedFollowupId) return []
    const selectedFollowup = followupsOfTargetProject.find((followup) => followup.id === watchedFollowupId)
    if (!selectedFollowup?.configUI?.medEventsUI) return []

    const medEventsSource = watchedProjectId === project.id ? medEvents : otherProjectData?.MedEvents || []

    return selectedFollowup.configUI.medEventsUI.map((medEventUI: TMedEventUI) => ({
      value: medEventUI.MedEvent.id,
      label: getTranslatedLabel(
        locale as TNavigationLocales,
        medEventsSource.find((medEvent) => medEvent.id === medEventUI.MedEvent.id)?.intlName,
      ),
    }))
  }, [
    watchedFollowupId,
    followupsOfTargetProject,
    locale,
    watchedProjectId,
    project.id,
    medEvents,
    otherProjectData,
  ])

  const isDifferentFollowup = watchedFollowupId !== sourceFollowupId
  const selectedProjectOption = PROJECT_OPTIONS.find((option) => option.value === watchedProjectId)
  const selectedFollowupOption = FOLLOWUP_OPTIONS.find((option) => option.value === watchedFollowupId)
  const selectedMedEventsOptions = MED_EVENT_OPTIONS.filter((option) =>
    watch('medEventIds').includes(option.value),
  )

  // Update followup selection when project changes
  useEffect(() => {
    if (watchedProjectId === project.id) {
      // When returning to the source project, select the message's own followup
      setValue('followupId', sourceFollowupId)
      setValue('medEventIds', [])
    } else {
      // When switching to another project, select first followup
      const firstFollowup = followupsOfTargetProject[0]
      if (firstFollowup) {
        setValue('followupId', firstFollowup.id)
      }
      // Reset medEvents
      setValue('medEventIds', [])
    }
  }, [watchedProjectId, project.id, followupsOfTargetProject, sourceFollowupId, setValue])

  const handleChangeProject = (item: CurecallSearchableSelectItem) => {
    clearErrors('projectId')
    setValue('projectId', item.value)
  }

  const handleChangeFollowup = (item: CurecallSearchableSelectItem) => {
    clearErrors('followupId')
    setValue('followupId', item.value)
    setValue('medEventIds', [])
  }

  const handleChangeMedEvents = (items: TOptionWithNumberValue[]) => {
    clearErrors('medEventIds')
    setValue(
      'medEventIds',
      items.map((item) => item.value),
    )
  }

  const handleConfirm = async (formData: TDuplicateMessageFormValues) => {
    setIsLoading(true)
    try {
      const response = await ProjectService.cloneSelfConfigurationMessage(project.id, message.id, {
        targetProjectId: formData.projectId,
        targetFollowupId: formData.followupId,
        medEventIds: formData.medEventIds,
      })

      notify({
        title: t('pages.configuration.messages.duplicate.success'),
        message: t('pages.configuration.messages.duplicate.success-message'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })

      // Invalidate caches for both list and timeline views of the target
      queryClient.invalidateQueries({ queryKey: ['programmed-messages', response.targetProjectId] })
      queryClient.invalidateQueries({
        queryKey: ['followup-self-configuration', response.targetProjectId, response.targetFollowupId],
      })

      // Redirect to the cloned message edit page
      navigate(
        `/${response.targetProjectId}/configuration/followups/${response.targetFollowupId}/messages/${response.id}`,
      )

      onClose()
    } catch (err) {
      notify({
        title: t('pages.configuration.messages.duplicate.error'),
        message: getErrorMessage(err),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <CurecallModalContainer
      isOpen={isOpen}
      toggle={onClose}
      closeModalAriaLabel={t('common.actions.close')}
      dialogClassName="c-max-w-[600px]"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleConfirm)}>
          <div className="c-p-6">
            <h3 className="c-mb-6">{t('pages.configuration.messages.duplicate.modal-title')}</h3>

            <div>
              {userProjects.length > 1 && (
                <Controller
                  control={control}
                  name="projectId"
                  render={() => (
                    <CurecallSearchableSelect
                      label={t('pages.configuration.messages.duplicate.modal-project-label')}
                      defaultItemSelected={selectedProjectOption}
                      onSelect={handleChangeProject}
                      items={PROJECT_OPTIONS}
                      placeholder={t('pages.configuration.messages.duplicate.modal-project-label')}
                      errorMessage={errors.projectId && t('common.form.required')}
                      wrapperClassName="c-pb-6"
                    />
                  )}
                />
              )}

              <Controller
                control={control}
                name="followupId"
                rules={{ required: true }}
                render={() => (
                  <CurecallSearchableSelect
                    label={t('pages.configuration.messages.duplicate.modal-followup-label')}
                    defaultItemSelected={selectedFollowupOption}
                    onSelect={handleChangeFollowup}
                    items={FOLLOWUP_OPTIONS}
                    placeholder={t('pages.configuration.messages.duplicate.modal-followup-label')}
                    isDisabled={followupsOfTargetProject.length === 0}
                    optionsClassName="c-max-h-[176px]"
                    wrapperClassName="c-pb-6"
                    errorMessage={errors.followupId && t('common.form.required')}
                  />
                )}
              />
              {followupsOfTargetProject.length === 0 && (
                <p className="c-text-sm c-text-red-600 c-mt-1 c-mb-6">
                  {t('pages.configuration.messages.duplicate.modal-no-followup')}
                </p>
              )}

              {isDifferentFollowup && MED_EVENT_OPTIONS.length > 0 && (
                <Controller
                  control={control}
                  name="medEventIds"
                  rules={{ required: true }}
                  render={() => (
                    <CurecallMultiSelect
                      label={t('pages.configuration.messages.duplicate.modal-medevents-label')}
                      isRequired={true}
                      items={MED_EVENT_OPTIONS}
                      selectedItems={selectedMedEventsOptions}
                      onSelect={handleChangeMedEvents}
                      selectedElementsLabel={`${selectedMedEventsOptions.length} ${t('pages.configuration.messages.selected')}`}
                      placeholder={t('pages.configuration.messages.duplicate.modal-medevents-placeholder')}
                      wrapperClassName="c-w-full c-relative"
                      errorMessage={errors.medEventIds && t('common.form.required')}
                    />
                  )}
                />
              )}

              <div className="c-flex c-justify-center c-gap-8 c-mt-32">
                <CurecallButton
                  type="button"
                  onClick={onClose}
                  appearance={ECurecallButtonAppearance.CANCEL}
                  disabled={isLoading}
                >
                  {t('pages.configuration.messages.duplicate.modal-cancel')}
                </CurecallButton>
                <CurecallButton
                  type="submit"
                  isLoading={isLoading}
                  disabled={
                    followupsOfTargetProject.length === 0 ||
                    (isDifferentFollowup && selectedMedEventsOptions.length === 0)
                  }
                >
                  {t('pages.configuration.messages.duplicate.modal-duplicate')}
                </CurecallButton>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </CurecallModalContainer>
  )
}
